import React, { useEffect, useState } from "react";
import {
  Privilege,
  getPrivilegesByRoleId,
  getPrivilegesByApplicationId,
} from "@/api/privileges";
import { Application, getApplication } from "@/api/applications";
import { Customer, getCustomer } from "@/api/customers";
import { User, getUser } from "@/api/users";
import { Role, getRole } from "@/api/roles";
import AppRolePrivilegeDetailList from "@/components/privileges/appRolePrivileges/AppRolePrivilegeDetailList";
import SearchBox from "@/ui-controls/SearchBox";
import { IconButton, MessageBar, MessageBarType } from "@fluentui/react";
import { useNavigate } from "react-router-dom";
import { useErrorNotification } from "@/utils/useErrorNotification";

interface Props {
  ApplicationId: number;
  CustomerId: number;
  UserId: number;
  RoleId: number;
  onRoleLoaded?: (name: string) => void;
  breadcrumbTitle?: string;
  breadcrumbSubtitle?: React.ReactNode;
  backPath?: string;
}

const AppCustomerUserRolePrivileges = (props: Props) => {
  const navigate = useNavigate();
  const { errorMessage, showError, clearError } = useErrorNotification();
  const [rolePrivileges, setRolePrivileges] = useState<Privilege[]>([]);
  const [filteredRolePrivileges, setFilteredRolePrivileges] = useState<
    Privilege[]
  >([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [isSearchPerformed, setIsSearchPerformed] = useState<boolean>(false);

  const [loadingRolePrivileges, setLoadingRolePrivileges] =
    useState<boolean>(false);
  const [loadingApplication, setLoadingApplication] = useState<boolean>(false);
  const [loadingCustomer, setLoadingCustomer] = useState<boolean>(false);
  const [loadingUser, setLoadingUser] = useState<boolean>(false);
  const [loadingRole, setLoadingRole] = useState<boolean>(false);

  const [application, setApplication] = useState<Application | undefined>(
    undefined
  );
  const [customer, setCustomer] = useState<Customer | undefined>(undefined);
  const [user, setUser] = useState<User | undefined>(undefined);
  const [role, setRole] = useState<Role | undefined>(undefined);

  useEffect(() => {
    if (props.ApplicationId) {
      getApplicationById(props.ApplicationId);
    }
  }, [props.ApplicationId]);

  useEffect(() => {
    if (props.CustomerId) {
      getCustomerById(props.CustomerId);
    }
  }, [props.CustomerId]);

  useEffect(() => {
    if (props.UserId) {
      getUserById(props.UserId);
    }
  }, [props.UserId]);

  useEffect(() => {
    if (props.RoleId) {
      getRolePrivileges(props.RoleId);
      getRoleById(props.RoleId);
    }
  }, [props.RoleId]);

  // useEffect(() => {
  //   getUnassignedAppPrivileges();
  // }, [applicationPrivileges, rolePrivileges]);

  const getRolePrivileges = (roleId: number) => {
    setLoadingRolePrivileges(true);
    getPrivilegesByRoleId(roleId)
      .then((result) => {
        setRolePrivileges(result);
        setLoadingRolePrivileges(false);
      })
      .catch((error) => {
        handleError(error);
        setLoadingRolePrivileges(false);
      });
  };

  // const getApplicationPrivileges = (applicationId: number) => {
  //   setLoadingAppPrivileges(true);
  //   getPrivilegesByApplicationId(applicationId)
  //     .then((result) => {
  //       setApplicationPrivileges(result);
  //       setLoadingAppPrivileges(false);
  //     })
  //     .catch((error) => {
  //       handleError(error);
  //       setLoadingAppPrivileges(false);
  //     });
  // };

  const handlePrivilegeSearch = (searchValue: string) => {
    setSearchValue(searchValue);
    const hasSearchValue = searchValue.trim().length > 0;
    setIsSearchPerformed(hasSearchValue);

    if (hasSearchValue) {
      const filteredPrivileges = rolePrivileges.filter(
        (privilege) =>
          privilege.Name.toLowerCase().includes(searchValue.toLowerCase()) ||
          (privilege.Description &&
            privilege.Description.toLowerCase().includes(
              searchValue.toLowerCase()
            ))
      );
      setFilteredRolePrivileges(filteredPrivileges);
    } else {
      setFilteredRolePrivileges([]);
    }
  };

  const handleRefresh = () => {
    setSearchValue("");
    setIsSearchPerformed(false);
    setFilteredRolePrivileges([]);
    getRolePrivileges(props.RoleId);
  };

  const getApplicationById = (applicationId: number) => {
    setLoadingApplication(true);
    getApplication(applicationId)
      .then((result) => {
        setApplication(result);
        setLoadingApplication(false);
      })
      .catch((error) => {
        handleError(error);
        setLoadingApplication(false);
      });
  };

  const getCustomerById = (customerId: number) => {
    setLoadingCustomer(true);
    getCustomer(customerId)
      .then((result) => {
        setCustomer(result);
        setLoadingCustomer(false);
      })
      .catch((error) => {
        handleError(error);
        setLoadingCustomer(false);
      });
  };

  const getUserById = (userId: number) => {
    setLoadingUser(true);
    getUser(userId)
      .then((result) => {
        setUser(result);
        setLoadingUser(false);
      })
      .catch((error) => {
        handleError(error);
        setLoadingUser(false);
      });
  };

  const getRoleById = (roleId: number) => {
    setLoadingRole(true);
    getRole(roleId)
      .then((result) => {
        setRole(result);
        if (props.onRoleLoaded && result?.Name) {
          props.onRoleLoaded(result.Name);
        }
        setLoadingRole(false);
      })
      .catch((error) => {
        handleError(error);
        setLoadingRole(false);
      });
  };

  // const getUnassignedAppPrivileges = () => {
  //   const privileges = applicationPrivileges.filter(
  //     (ap) => !rolePrivileges.find((rp) => rp.Id === ap.Id)
  //   );
  //   setUnassignedAppPrivileges(privileges);
  // };

  // const resfreshRolePrivileges = (privilegeIds: number[]) => {
  //   const newAssignedPrivileges = applicationPrivileges.filter((ap) =>
  //     privilegeIds.find((pid) => pid === ap.Id)
  //   );

  //   const newRolePrivileges = [...rolePrivileges, ...newAssignedPrivileges];
  //   if (props.onAddRolePrivileges) props.onAddRolePrivileges(newRolePrivileges);
  //   setRolePrivileges(newRolePrivileges);
  // };

  const handleError = (error: any) => {
    showError(error);
  };

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb-left-section">
          {props.backPath && (
            <IconButton
              iconProps={{ iconName: "Back" }}
              title="Back to User Roles"
              onClick={() => navigate(props.backPath!)}
              styles={{
                root: { width: 40, height: 40 },
                rootHovered: { backgroundColor: "#f3f2f1" },
                icon: { fontSize: 18, color: "#0078d4" },
                rootPressed: { backgroundColor: "#e1dfdd" },
              }}
            />
          )}
          <div>
            {props.breadcrumbTitle && (
              <h2 className="page-breadcrumb-title">
                {props.breadcrumbTitle}
              </h2>
            )}
            {props.breadcrumbSubtitle && (
              <div style={{ color: "#605e5c", fontSize: "14px" }}>
                {props.breadcrumbSubtitle}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <SearchBox placeholder="Search" onSearch={handlePrivilegeSearch} />
          <IconButton
            iconProps={{ iconName: "Refresh" }}
            title="Refresh"
            onClick={handleRefresh}
            styles={{
              root: { width: 40, height: 40 },
              rootHovered: { backgroundColor: "#f3f2f1" },
              icon: { fontSize: 18, color: "#0078d4" },
              rootPressed: { backgroundColor: "#e1dfdd" },
            }}
          />
        </div>
      </div>

      <div>
        {role ? (
          <AppRolePrivilegeDetailList
            role={role}
            privilegeList={
              isSearchPerformed
                ? filteredRolePrivileges.length === 0
                  ? []
                  : filteredRolePrivileges
                : rolePrivileges
            }
            onRemovePrivileges={() => getRolePrivileges(props.RoleId)}
            onDelete={(_) => {
              getRolePrivileges(props.RoleId);
            }}
          />
        ) : null}
      </div>
      {errorMessage && (
        <MessageBar
          messageBarType={MessageBarType.error}
          isMultiline={false}
          onDismiss={clearError}
          styles={{
            root: {
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              zIndex: 1000,
            },
          }}
        >
          {errorMessage}
        </MessageBar>
      )}
    </>
  );
};

export default AppCustomerUserRolePrivileges;
